<template>
  <hr-form-dialog
    ref="dialog"
    size="lg"
    maximize
    :maximized="maximized"
    :title="$t('sessions.noteTemplate.editSessionNoteTemplate.layout.title')"
    @update:maximized="$emit('update:maximized', $event)"
    @close="hide"
    @hide="onDialogHide"
    @submit="onSaveNoteTemplateLayout"
  >
    <p>{{ $t('sessions.noteTemplate.editSessionNoteTemplate.layout.editLayoutDescription') }}</p>

    <hr-card-section class="row q-pt-none q-col-gutter-md">
      <hr-on-off-switch
        v-model="editLayoutSettings.showPageHeader"
        :label="$t('sessions.noteTemplate.editSessionNoteTemplate.layout.sessionNoteHeader')"
      />

      <div
        v-if="editLayoutSettings.showPageHeader"
        class="col-12"
      >
        <text-editor
          ref="pageHeaderContent"
          v-model="editHeaderContentText"
          indent
          enable-dynamic-fields
          checklist
          :wrapper-class="wrapperClass"
          @error-status-update="(status) => setValidationError(status, 'pageHeaderContent')"
        >
          <text-editor-fields-toolbar
            :label="$t('common.buttonDynamicFields')"
            :field-categories="sessionNoteLayoutFields"
            no-custom-fields
            @insert="onInsertToken('pageHeaderContent', $event)"
          />
        </text-editor>
      </div>
    </hr-card-section>
    <hr-card-section class="row q-col-gutter-md">
      <hr-on-off-switch
        v-model="editLayoutSettings.showPageFooter"
        :label="$t('sessions.noteTemplate.editSessionNoteTemplate.layout.sessionNoteFooter')"
      />

      <div
        v-if="editLayoutSettings.showPageFooter"
        class="col-12"
      >
        <text-editor
          ref="pageFooterContent"
          v-model="editFooterContentText"
          indent
          enable-dynamic-fields
          checklist
          :wrapper-class="wrapperClass"
          @error-status-update="(status) => setValidationError(status, 'pageFooterContent')"
        >
          <text-editor-fields-toolbar
            :label="$t('common.buttonDynamicFields')"
            :field-categories="sessionNoteLayoutFields"
            no-custom-fields
            @insert="onInsertToken('pageFooterContent', $event)"
          />
        </text-editor>
      </div>
    </hr-card-section>

    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        type="submit"
        :disable="!networkOnline || isValidationError"
        :label="$t('common.buttonSave')"
        data-e2e="button-save"
      />
    </template>
  </hr-form-dialog>
</template>

<script>

import TextEditor from 'components/UI/TextEditor.vue';
import TextEditorFieldsToolbar from 'components/UI/TextEditorFieldsToolbar.vue';
import { chain, flatMap, isEmpty } from 'lodash';
import { mapState } from 'pinia';
import { HrConfirmBtn, HrDismissBtn, HrFormDialog, HrOnOffSwitch } from 'src/design-system';
/*
 * The one remaining deep import into the design system, and there is nothing on the barrel to swap
 * it for: HrCardSection is `@status internal` with no `@useInstead`, and this file is its only
 * consumer anywhere. Under a design-system theme it renders a div with 16px of padding and nothing
 * else, so a plain div is the eventual replacement — but not a one-liner, because `cssAddon` is off
 * in quasar.conf.js, so `q-pa-md` does not exist in the loaded CSS. That is also why the
 * `q-pt-none` on the first section above does nothing today.
 */
// eslint-disable-next-line import/no-restricted-paths -- see above
import { HrCardSection } from 'src/design-system/migration';
import ClinicSettingsDB from 'src/fb/clinicSettings-db';
import Interactions from 'src/mixins/Interactions';
import SessionNotesTemplates from 'src/mixins/SessionNotesTemplates';
import { network } from 'src/store/network';

export default {
  name: 'EditSessionNotesLayout',

  components: {
    TextEditorFieldsToolbar,
    TextEditor,
    HrFormDialog,
    HrDismissBtn,
    HrConfirmBtn,
    HrCardSection,
    HrOnOffSwitch,
  },

  mixins: [SessionNotesTemplates, Interactions],

  props: {
    layoutSettings: {
      type: Object,
      required: true,
    },
    maximized: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['hide', 'ok', 'update:maximized'],

  data() {
    return {
      editLayoutSettings: {
        showPageHeader: false,
        pageHeaderContent: '',
        showPageFooter: false,
        pageFooterContent: '',
      },
      fieldErrorsPresent: [],
      editHeaderContentText: '',
      editFooterContentText: '',
    };
  },

  computed: {
    ...mapState(network, ['networkOnline']),

    isMobileView() {
      return this.$q.platform.is.mobile || this.$q.screen.lt.sm;
    }, // isMobileView()

    wrapperClass() {
      return this.isMobileView
        ? 'q-my-none custom-text-editor custom-text-editor--mobile'
        : 'q-my-none custom-text-editor';
    }, // wrapperClass()

    isLayoutEnabled() {
      return this.editLayoutSettings.showPageHeader || this.editLayoutSettings.showPageFooter;
    }, // isLayoutEnabled()

    /** @return {Field[]} */
    noteTemplateLayoutFieldPresets() {
      return this.sessionNoteFieldPresets
        .filter((field) => !field.prompt && field.category !== 'user');
    }, // noteTemplateLayoutFieldPresets()

    tokenFieldCategories() {
      return chain(this.noteTemplateLayoutFieldPresets)
        .map((fieldPreset) => ({
          category: fieldPreset.category,
          token: fieldPreset.token,
          label: fieldPreset.label,
          tokenType: 'dynamicField',
          canEdit: false,
          type: 'preset',
          id: null,
          hiddenFromSelection: !!fieldPreset.hiddenFromSelection,
        }))
        // Prevent null in groupBy as browser *can* handle it differently
        .groupBy('category')
        .map((fields) => this.sessionNoteFieldPresetToCategories(fields))
        .value();
    }, // tokenFieldCategories

    sessionNoteLayoutFields() {
      return [
        this.sessionNoteLayoutFieldCategory,
        ...this.tokenFieldCategories,
      ];
    }, // sessionNoteLayoutFields()

    isValidationError() {
      return !!this.fieldErrorsPresent.length;
    },

  },

  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      if (!isEmpty(this.layoutSettings)) {
        this.editLayoutSettings = { ...this.layoutSettings };
        this.editHeaderContentText = this.editLayoutSettings.pageHeaderContent;
        this.editFooterContentText = this.editLayoutSettings.pageFooterContent;
      }

      const layoutFields = flatMap(this.sessionNoteLayoutFields, 'fields');

      this.editHeaderContentText = this.convertTokensToHtml(layoutFields, this.editHeaderContentText);
      this.editFooterContentText = this.convertTokensToHtml(layoutFields, this.editFooterContentText);

      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    setValidationError(status, errorName) {
      const errorIndex = this.fieldErrorsPresent.indexOf(errorName);
      if (!status && (errorIndex + 1)) {
        this.fieldErrorsPresent.splice(errorIndex, 1);
      } else if (status && !(errorIndex + 1)) {
        this.fieldErrorsPresent.push(errorName);
      }
    }, // setValidationError()

    async onSaveNoteTemplateLayout() {
      const clinicSettingsDb = new ClinicSettingsDB();

      const layoutFields = flatMap(this.sessionNoteLayoutFields, 'fields');

      this.editLayoutSettings.pageHeaderContent = this.convertHtmlToTokens(layoutFields, this.editHeaderContentText);
      this.editLayoutSettings.pageFooterContent = this.convertHtmlToTokens(layoutFields, this.editFooterContentText);

      this.loadingLayoutSettings = true;
      try {
        await clinicSettingsDb.set(
          { sessionNotesLayoutSettings: this.editLayoutSettings },
          this.connectedClinic.id,
        );

        this.notifySuccess(this.$t('sessions.noteTemplate.editSessionNoteTemplate.layout.successSavingLayoutSettings'));
        this.$emit('ok', { updatedLayoutSettings: this.editLayoutSettings });
        this.hide();
      } catch (err) {
        this.notifyNegative(`${
          this.$t('sessions.noteTemplate.editSessionNoteTemplate.layout.errorSavingLayoutSettings')
        }: ${err.message}`);
      } finally {
        this.loadingLayoutSettings = false;
      }
    },

    onInsertToken(ref, tokenField) {
      const editorRef = this.$refs?.[ref];
      if (!editorRef) {
        return;
      }

      editorRef.insertDynamicField(
        this.getTokenHtml(tokenField.token, tokenField.label, tokenField.tokenType),
      );
    }, // onInsertToken()

  },
};
</script>

<style scoped lang="scss">
.custom-transition {
  transition: width 0.15s linear, height 0.15s linear;
}
.edit-dialog {
  width: 100%;
  max-width: 1140px;
  overflow-x: hidden;
}
</style>
